import React from 'react';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import Mail from 'lucide-react/dist/esm/icons/mail.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import { useTranslations } from '../contexts/TranslationContext';
import { DRAW_LIFECYCLE_STATES, getDrawLifecycleCopy } from '../utils/drawLifecycle';

const toneClasses = {
  [DRAW_LIFECYCLE_STATES.WAITING]: {
    wrap: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100',
    icon: 'text-blue-600 dark:text-blue-300',
    body: 'text-blue-700 dark:text-blue-200',
  },
  [DRAW_LIFECYCLE_STATES.STARTED]: {
    wrap: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-300',
    body: 'text-amber-700 dark:text-amber-200',
  },
  [DRAW_LIFECYCLE_STATES.EXPIRED]: {
    wrap: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-300',
    body: 'text-amber-700 dark:text-amber-200',
  },
  [DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING]: {
    wrap: 'border-indigo-200 bg-indigo-50 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-100',
    icon: 'text-indigo-600 dark:text-indigo-300',
    body: 'text-indigo-700 dark:text-indigo-200',
  },
  [DRAW_LIFECYCLE_STATES.FINISHED]: {
    wrap: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100',
    icon: 'text-emerald-600 dark:text-emerald-300',
    body: 'text-emerald-700 dark:text-emerald-200',
  },
  [DRAW_LIFECYCLE_STATES.REOPENED]: {
    wrap: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-100',
    icon: 'text-sky-600 dark:text-sky-300',
    body: 'text-sky-700 dark:text-sky-200',
  },
};

function StatusIcon({ state, className }) {
  if (state === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING) return <Mail className={className} />;
  if (state === DRAW_LIFECYCLE_STATES.FINISHED) return <Trophy className={className} />;
  if (state === DRAW_LIFECYCLE_STATES.WAITING || state === DRAW_LIFECYCLE_STATES.REOPENED) return <Clock className={className} />;
  return <Loader2 className={`${className} animate-spin`} />;
}

export default function DrawLifecycleMessage({
  state,
  compact = false,
  className = '',
  showBody = true,
}) {
  const { t } = useTranslations();
  if (!state) return null;

  const copy = getDrawLifecycleCopy(state);
  const tone = toneClasses[state] || toneClasses[DRAW_LIFECYCLE_STATES.EXPIRED];
  const title = t(copy.titleKey, copy.titleFallback);
  const body = t(copy.bodyKey, copy.bodyFallback);

  return (
    <div className={`rounded-xl border ${tone.wrap} ${compact ? 'px-4 py-3' : 'px-5 py-4'} ${className}`} role="status" aria-live="polite">
      <div className="flex items-center justify-center gap-3 text-center">
        <StatusIcon state={state} className={`h-5 w-5 flex-shrink-0 ${tone.icon}`} />
        <div>
          <p className={`${compact ? 'text-sm' : 'text-base'} font-bold`}>{title}</p>
          {showBody && (
            <p className={`${compact ? 'text-xs' : 'text-sm'} ${tone.body}`}>{body}</p>
          )}
        </div>
      </div>
    </div>
  );
}